import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, Calendar, Check, Trophy, Upload, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatMatchDate } from "@/lib/dates";

const AdminMatches = () => {
  const [matches, setMatches] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [filterComp, setFilterComp] = useState("all");
  const [formData, setFormData] = useState({
    competition_id: "",
    home_team_id: "",
    away_team_id: "",
    match_date: "",
    match_time: "",
    stadium: "",
    matchday: "",
    referee: "",
    round: "",
    next_match_id: "",
    next_match_slot: "",
  });
  const [resultData, setResultData] = useState({
    home_score: 0,
    away_score: 0
  });
  const [isScaffoldOpen, setIsScaffoldOpen] = useState(false);
  const [scaffoldData, setScaffoldData] = useState({ starting_round: "R32", include_third_place: true });
  const [scaffolding, setScaffolding] = useState(false);
  // Bulk import state
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [bulkComp, setBulkComp] = useState("");
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [matchesRes, compsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/matches`),
        axios.get(`${API}/competitions`),
        axios.get(`${API}/teams`)
      ]);
      setMatches(matchesRes.data);
      setCompetitions(compsRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const teamsMap = {};
  teams.forEach(t => { teamsMap[t.team_id] = t; });

  const compsMap = {};
  competitions.forEach(c => { compsMap[c.competition_id] = c; });

  const filteredMatches = filterComp === "all" 
    ? matches 
    : matches.filter(m => m.competition_id === filterComp);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        matchday: formData.matchday ? parseInt(formData.matchday) : null,
        round: formData.round || null,
        next_match_id: formData.next_match_id || null,
        next_match_slot: formData.next_match_slot || null,
      };

      if (editingMatch) {
        await axios.patch(`${API}/matches/${editingMatch.match_id}`, payload, { withCredentials: true });
        toast.success("Match updated");
      } else {
        await axios.post(`${API}/matches`, payload, { withCredentials: true });
        toast.success("Match created successfully");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error(error.response?.data?.detail || "Failed to save match");
    }
  };

  const openEditDialog = (match) => {
    setEditingMatch(match);
    setFormData({
      competition_id: match.competition_id || "",
      home_team_id: match.home_team_id || "",
      away_team_id: match.away_team_id || "",
      match_date: match.match_date || "",
      match_time: match.match_time || "",
      stadium: match.stadium || "",
      matchday: match.matchday ? String(match.matchday) : "",
      referee: match.referee || "",
      round: match.round || "",
      next_match_id: match.next_match_id || "",
      next_match_slot: match.next_match_slot || "",
    });
    setIsDialogOpen(true);
  };

  const handleResultSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMatch) return;

    try {
      await axios.put(
        `${API}/matches/${selectedMatch.match_id}/result`,
        {
          home_score: parseInt(resultData.home_score),
          away_score: parseInt(resultData.away_score),
          status: "completed"
        },
        { withCredentials: true }
      );
      toast.success("Match result saved - standings updated!");
      setIsResultDialogOpen(false);
      setSelectedMatch(null);
      setResultData({ home_score: 0, away_score: 0 });
      fetchData();
    } catch (error) {
      console.error("Error saving result:", error);
      toast.error("Failed to save result");
    }
  };

  const handleDelete = async (matchId) => {
    if (!window.confirm("Are you sure you want to delete this match?")) return;
    
    try {
      await axios.delete(`${API}/matches/${matchId}`, { withCredentials: true });
      toast.success("Match deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting match:", error);
      toast.error("Failed to delete match");
    }
  };

  const filterCompObj = compsMap[filterComp];
  const isCupFilterSelected = filterCompObj?.type === "cup";
  const cupMatchCount = filterCompObj ? matches.filter((m) => m.competition_id === filterComp).length : 0;

  const handleScaffold = async () => {
    if (!isCupFilterSelected) return;
    if (cupMatchCount > 0) {
      toast.error("This cup already has matches — delete them first to rebuild the skeleton.");
      return;
    }
    if (!window.confirm(
      `Build empty bracket skeleton for ${filterCompObj.name}?\n\n` +
      `Starting round: ${scaffoldData.starting_round}\n` +
      `Includes 3rd place: ${scaffoldData.include_third_place ? "Yes" : "No"}\n\n` +
      `This creates empty matches for every subsequent round with next-match links pre-wired.`
    )) return;
    setScaffolding(true);
    try {
      const res = await axios.post(
        `${API}/admin/competitions/${filterComp}/bracket/scaffold`,
        scaffoldData,
        { withCredentials: true }
      );
      toast.success(`Bracket built — ${res.data.matches_created} matches created`);
      setIsScaffoldOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to build bracket");
    } finally {
      setScaffolding(false);
    }
  };

  // ---- Bulk match CSV import ----
  const parseCsv = (text) => {
    const lines = text.replace(/\r/g, "").split("\n").filter((l) => l.trim());
    if (!lines.length) return [];
    const parseLine = (line) => {
      const out = [];
      let cur = "", inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
          else inQ = !inQ;
        } else if (ch === "," && !inQ) { out.push(cur.trim()); cur = ""; }
        else cur += ch;
      }
      out.push(cur.trim());
      return out;
    };
    const header = parseLine(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, "_"));
    return lines.slice(1).map((line) => {
      const cells = parseLine(line);
      const row = {};
      header.forEach((h, i) => { row[h] = cells[i] ?? ""; });
      return row;
    });
  };

  const handleBulkFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFileName(file.name);
    setBulkResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCsv(ev.target.result || "");
      setBulkRows(rows);
      if (!rows.length) toast.error("CSV parsed but contained no data rows");
    };
    reader.readAsText(file);
  };

  const downloadMatchTemplate = () => {
    const csv = "home,away,match_date,match_time,stadium,matchday,round,status,referee,home_score,away_score,source_match_id\n" +
                "ASU,PAR,2026-09-20,19:00,ABFA Technical Centre,1,,scheduled,John Pierre,,,ABFA-2026-001\n" +
                "VIL,OLD,2026-09-21,16:00,YMCA Grounds,1,,completed,M. Francis,2,1,ABFA-2026-002\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "abfa-matches-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitBulkMatches = async (opts = {}) => {
    const dryRun = !!opts.dryRun;
    const updateExisting = !!opts.updateExisting;
    if (!bulkComp) { toast.error("Select a competition first"); return; }
    if (!bulkRows.length) { toast.error("Load a CSV file first"); return; }
    setBulkLoading(true); setBulkResults(null);
    try {
      const payload = {
        competition_id: bulkComp,
        dry_run: dryRun,
        update_existing: updateExisting,
        matches: bulkRows.map((r) => ({
          home: r.home || "",
          away: r.away || "",
          match_date: r.match_date || "",
          match_time: r.match_time || null,
          stadium: r.stadium || null,
          matchday: r.matchday ? parseInt(r.matchday, 10) : null,
          round: (r.round || "").toUpperCase() || null,
          status: (r.status || "scheduled").toLowerCase(),
          referee: r.referee || null,
          source_match_id: r.source_match_id || null,
          home_score: r.home_score !== "" && r.home_score != null ? parseInt(r.home_score, 10) : null,
          away_score: r.away_score !== "" && r.away_score != null ? parseInt(r.away_score, 10) : null,
        })),
      };
      const { data } = await axios.post(`${API}/matches/bulk`, payload, { withCredentials: true });
      setBulkResults(data);
      const c = data.counts || {};
      if (dryRun) {
        toast.info(`Dry run: ${c.created} would be created, ${c.updated} updated, ${c.skipped} skipped, ${c.duplicate_in_csv} CSV dupes, ${c.errors} errors`);
      } else {
        if (c.created > 0 || c.updated > 0) {
          toast.success(`${c.created} created · ${c.updated} updated · ${c.skipped} skipped · ${c.duplicate_in_csv} CSV dupes`);
          fetchData();
        }
        if (c.errors > 0) {
          toast.warning(`${c.errors} rows had errors — see details below`);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const resetBulk = () => {
    setBulkRows([]); setBulkFileName(""); setBulkResults(null);
  };

  const openResultDialog = (match) => {
    setSelectedMatch(match);
    setResultData({
      home_score: match.home_score || 0,
      away_score: match.away_score || 0
    });
    setIsResultDialogOpen(true);
  };

  const resetForm = () => {
    setEditingMatch(null);
    setFormData({
      competition_id: "",
      home_team_id: "",
      away_team_id: "",
      match_date: "",
      match_time: "",
      stadium: "",
      matchday: "",
      referee: "",
      round: "",
      next_match_id: "",
      next_match_slot: "",
    });
  };

  const formatDate = (dateStr) => formatMatchDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <AdminLayout title="Matches">
      <div data-testid="admin-matches">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <p className="text-gray-500">Manage fixtures and results</p>
            <Select value={filterComp} onValueChange={setFilterComp}>
              <SelectTrigger className="w-48 rounded-none" data-testid="filter-comp-select">
                <SelectValue placeholder="All Competitions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Competitions</SelectItem>
                {competitions.map(comp => (
                  <SelectItem key={comp.competition_id} value={comp.competition_id}>{comp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={isBulkOpen} onOpenChange={(open) => { setIsBulkOpen(open); if (!open) resetBulk(); }}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-none"
                  data-testid="bulk-import-matches-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Bulk Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading font-bold uppercase">Bulk Import Matches</DialogTitle>
                </DialogHeader>
                <div className="space-y-4" data-testid="bulk-matches-form">
                  <div className="p-3 bg-blue-50 border-l-4 border-blue-500 text-xs text-gray-700">
                    Upload a CSV of fixtures/results. Teams are resolved by <strong>short_name</strong> or <strong>name</strong> (case-insensitive).
                    Columns: <code>home, away, match_date, match_time, stadium, matchday, round, status, referee, home_score, away_score</code>.
                    Rows with <code>home_score</code>/<code>away_score</code> are saved as <em>completed</em> results.
                  </div>
                  <div>
                    <label className="text-sm font-medium">Competition</label>
                    <Select value={bulkComp} onValueChange={setBulkComp}>
                      <SelectTrigger className="rounded-none" data-testid="bulk-matches-comp-select">
                        <SelectValue placeholder="Select competition…" />
                      </SelectTrigger>
                      <SelectContent>
                        {competitions.map((c) => (
                          <SelectItem key={c.competition_id} value={c.competition_id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-2 bg-[#09090b] text-white px-3 py-2 text-xs uppercase font-bold tracking-widest hover:bg-[#FFC72C] hover:text-black transition">
                      <FileText className="w-3.5 h-3.5" /> Choose CSV
                      <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleBulkFile} data-testid="bulk-matches-file-input" />
                    </label>
                    <Button type="button" variant="outline" size="sm" onClick={downloadMatchTemplate} className="rounded-none" data-testid="bulk-matches-template-btn">
                      Download template
                    </Button>
                    {bulkFileName && <span className="text-xs text-gray-600">{bulkFileName} · {bulkRows.length} rows</span>}
                  </div>

                  {bulkRows.length > 0 && !bulkResults && (
                    <div className="space-y-2">
                      <Button
                        onClick={() => submitBulkMatches({ dryRun: true })}
                        disabled={bulkLoading}
                        variant="outline"
                        className="w-full rounded-none"
                        data-testid="bulk-matches-dryrun-btn"
                      >
                        {bulkLoading ? "Checking…" : "Dry Run (preview without saving)"}
                      </Button>
                      <Button
                        onClick={() => submitBulkMatches({ dryRun: false, updateExisting: false })}
                        disabled={bulkLoading}
                        className="w-full bg-[#FFC72C] hover:bg-[#e6b325] text-black rounded-none"
                        data-testid="bulk-matches-submit-btn"
                      >
                        {bulkLoading ? "Importing…" : `Import ${bulkRows.length} rows (skip existing)`}
                      </Button>
                      <Button
                        onClick={() => { if (window.confirm("Update existing matches with CSV values? This overwrites current data for matches already in the DB.")) submitBulkMatches({ dryRun: false, updateExisting: true }); }}
                        disabled={bulkLoading}
                        variant="outline"
                        className="w-full rounded-none border-amber-400 text-amber-700 hover:bg-amber-50"
                        data-testid="bulk-matches-update-btn"
                      >
                        {bulkLoading ? "Importing…" : `Import + update existing`}
                      </Button>
                    </div>
                  )}

                  {bulkResults && (
                    <div className="space-y-2 text-xs" data-testid="bulk-matches-results">
                      {bulkResults.dry_run && (
                        <p className="font-bold text-blue-700 bg-blue-50 border-l-4 border-blue-500 p-2">
                          DRY RUN — nothing was saved. Review below.
                        </p>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className="bg-green-50 border border-green-200 p-2">
                          <p className="text-[10px] uppercase text-green-700">Created</p>
                          <p className="text-lg font-bold text-green-900" data-testid="bulk-count-created">{bulkResults.counts?.created ?? 0}</p>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 p-2">
                          <p className="text-[10px] uppercase text-amber-700">Updated</p>
                          <p className="text-lg font-bold text-amber-900" data-testid="bulk-count-updated">{bulkResults.counts?.updated ?? 0}</p>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 p-2">
                          <p className="text-[10px] uppercase text-gray-700">Skipped (dupe)</p>
                          <p className="text-lg font-bold text-gray-900" data-testid="bulk-count-skipped">{bulkResults.counts?.skipped ?? 0}</p>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 p-2">
                          <p className="text-[10px] uppercase text-gray-700">CSV dupes</p>
                          <p className="text-lg font-bold text-gray-900" data-testid="bulk-count-csv-dupes">{bulkResults.counts?.duplicate_in_csv ?? 0}</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 p-2">
                          <p className="text-[10px] uppercase text-red-700">Errors</p>
                          <p className="text-lg font-bold text-red-900" data-testid="bulk-count-errors">{bulkResults.counts?.errors ?? 0}</p>
                        </div>
                      </div>
                      {bulkResults.errors?.length > 0 && (
                        <div className="max-h-40 overflow-y-auto border border-red-200 bg-red-50 p-2">
                          <p className="font-bold text-red-800 mb-1">Errors</p>
                          {bulkResults.errors.map((e, i) => (
                            <p key={i} className="text-red-700">Row {e.row}: {e.detail}</p>
                          ))}
                        </div>
                      )}
                      {bulkResults.skipped?.length > 0 && (
                        <div className="max-h-32 overflow-y-auto border border-gray-200 bg-gray-50 p-2">
                          <p className="font-bold text-gray-800 mb-1">Skipped (already exist)</p>
                          {bulkResults.skipped.map((s, i) => (
                            <p key={i} className="text-gray-600">Row {s.row}: {s.detail || "Duplicate"}</p>
                          ))}
                        </div>
                      )}
                      {bulkResults.duplicate_in_csv?.length > 0 && (
                        <div className="max-h-32 overflow-y-auto border border-gray-200 bg-gray-50 p-2">
                          <p className="font-bold text-gray-800 mb-1">Duplicates within your CSV</p>
                          {bulkResults.duplicate_in_csv.map((d, i) => (
                            <p key={i} className="text-gray-600">Row {d.row}: {d.detail}</p>
                          ))}
                        </div>
                      )}
                      <Button
                        onClick={resetBulk}
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        data-testid="bulk-matches-reset-btn"
                      >
                        Start Over
                      </Button>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {isCupFilterSelected && (
              <Dialog open={isScaffoldOpen} onOpenChange={setIsScaffoldOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-none border-[#009E60] text-[#009E60] hover:bg-[#009E60] hover:text-white"
                    data-testid="build-bracket-btn"
                  >
                    <Trophy className="w-4 h-4 mr-2" /> Build Bracket
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading font-bold uppercase">
                      Build Bracket Skeleton
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4" data-testid="scaffold-form">
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-500 text-xs text-gray-700">
                      Creates empty matches for every round from the starting round down to the Final,
                      with <strong>next-match links pre-wired</strong>. Winners of each match will
                      auto-populate the next round. You then just fill in the starting-round team pairings.
                    </div>
                    <div>
                      <label className="text-sm font-medium">Starting Round</label>
                      <Select
                        value={scaffoldData.starting_round}
                        onValueChange={(v) => setScaffoldData({ ...scaffoldData, starting_round: v })}
                      >
                        <SelectTrigger className="rounded-none" data-testid="scaffold-round-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="R64">Round of 64 · 63 matches total</SelectItem>
                          <SelectItem value="R32">Round of 32 · 31 matches total</SelectItem>
                          <SelectItem value="R16">Round of 16 · 15 matches total</SelectItem>
                          <SelectItem value="QF">Quarter-Finals · 7 matches total</SelectItem>
                          <SelectItem value="SF">Semi-Finals · 3 matches total</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scaffoldData.include_third_place}
                        onChange={(e) => setScaffoldData({ ...scaffoldData, include_third_place: e.target.checked })}
                        className="accent-[#FFC72C]"
                        data-testid="scaffold-3po-checkbox"
                      />
                      Include 3rd-place play-off
                    </label>
                    {cupMatchCount > 0 && (
                      <p className="text-xs text-red-600" data-testid="scaffold-blocked-msg">
                        This cup already has {cupMatchCount} match{cupMatchCount === 1 ? "" : "es"}. Delete existing matches before scaffolding.
                      </p>
                    )}
                    <Button
                      onClick={handleScaffold}
                      disabled={scaffolding || cupMatchCount > 0}
                      className="w-full bg-[#009E60] hover:bg-[#008050] text-white rounded-none"
                      data-testid="scaffold-submit-btn"
                    >
                      {scaffolding ? "Building…" : "Build Skeleton"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-match-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Match
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase" data-testid="match-dialog-title">
                  {editingMatch ? "Edit Match" : "Add New Match"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="match-form">
                <div>
                  <label className="text-sm font-medium">Competition *</label>
                  <Select value={formData.competition_id} onValueChange={(v) => setFormData({...formData, competition_id: v})}>
                    <SelectTrigger className="rounded-none" data-testid="match-comp-select">
                      <SelectValue placeholder="Select Competition" />
                    </SelectTrigger>
                    <SelectContent>
                      {competitions.map(comp => (
                        <SelectItem key={comp.competition_id} value={comp.competition_id}>{comp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Home Team *</label>
                    <Select value={formData.home_team_id} onValueChange={(v) => setFormData({...formData, home_team_id: v})}>
                      <SelectTrigger className="rounded-none" data-testid="match-home-team-select">
                        <SelectValue placeholder="Select Team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Away Team *</label>
                    <Select value={formData.away_team_id} onValueChange={(v) => setFormData({...formData, away_team_id: v})}>
                      <SelectTrigger className="rounded-none" data-testid="match-away-team-select">
                        <SelectValue placeholder="Select Team" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map(team => (
                          <SelectItem key={team.team_id} value={team.team_id}>{team.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Match Date *</label>
                    <Input
                      name="match_date"
                      type="date"
                      value={formData.match_date}
                      onChange={handleChange}
                      required
                      className="rounded-none"
                      data-testid="match-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Match Time</label>
                    <Input
                      name="match_time"
                      type="time"
                      value={formData.match_time}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="match-time-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Stadium</label>
                    <Input
                      name="stadium"
                      value={formData.stadium}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="match-stadium-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Matchday</label>
                    <Input
                      name="matchday"
                      type="number"
                      value={formData.matchday}
                      onChange={handleChange}
                      className="rounded-none"
                      data-testid="match-matchday-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Referee</label>
                  <Input
                    name="referee"
                    value={formData.referee || ""}
                    onChange={handleChange}
                    className="rounded-none"
                    placeholder="e.g. John Pierre"
                    data-testid="match-referee-input"
                  />
                </div>

                {/* Knockout bracket fields (only meaningful for cup competitions) */}
                {compsMap[formData.competition_id]?.type === "cup" && (
                  <div className="border-t border-gray-200 pt-4 space-y-4" data-testid="bracket-fields">
                    <p className="text-xs font-bold uppercase text-gray-500 tracking-widest">Bracket · Cup Knockout</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Round</label>
                        <Select value={formData.round} onValueChange={(v) => setFormData({ ...formData, round: v })}>
                          <SelectTrigger className="rounded-none" data-testid="match-round-select">
                            <SelectValue placeholder="Select round" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PRELIM">Preliminary Round</SelectItem>
                            <SelectItem value="R64">Round of 64</SelectItem>
                            <SelectItem value="R32">Round of 32</SelectItem>
                            <SelectItem value="R16">Round of 16</SelectItem>
                            <SelectItem value="QF">Quarter-Finals</SelectItem>
                            <SelectItem value="SF">Semi-Finals</SelectItem>
                            <SelectItem value="F">Final</SelectItem>
                            <SelectItem value="3PO">3rd Place</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Winner → Slot</label>
                        <Select value={formData.next_match_slot} onValueChange={(v) => setFormData({ ...formData, next_match_slot: v })}>
                          <SelectTrigger className="rounded-none" data-testid="match-next-slot-select">
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="home">Home slot</SelectItem>
                            <SelectItem value="away">Away slot</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Winner Advances To (Next Match)</label>
                      <Select
                        value={formData.next_match_id}
                        onValueChange={(v) => setFormData({ ...formData, next_match_id: v })}
                      >
                        <SelectTrigger className="rounded-none" data-testid="match-next-match-select">
                          <SelectValue placeholder="Link to a later-round match (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {matches
                            .filter((m) => m.competition_id === formData.competition_id && m.match_id !== editingMatch?.match_id)
                            .map((m) => (
                              <SelectItem key={m.match_id} value={m.match_id}>
                                {(m.round || "—")} · {teamsMap[m.home_team_id]?.short_name || "TBD"} vs {teamsMap[m.away_team_id]?.short_name || "TBD"}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-gray-500 mt-1">
                        When this match is decided, the winner will be auto-placed into the selected slot of the linked match.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="match-submit-btn">
                    {editingMatch ? "Save Changes" : "Create Match"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="rounded-none">
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Result Dialog */}
        <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold uppercase">Enter Match Result</DialogTitle>
            </DialogHeader>
            {selectedMatch && (
              <form onSubmit={handleResultSubmit} className="space-y-6" data-testid="result-form">
                <div className="flex items-center justify-between gap-4">
                  <div className="text-center flex-1">
                    <img 
                      src={teamsMap[selectedMatch.home_team_id]?.logo_url || 'https://placehold.co/60x60/09090b/FFF?text=H'} 
                      alt="" 
                      className="w-16 h-16 mx-auto mb-2 object-contain"
                    />
                    <p className="font-heading font-bold">{teamsMap[selectedMatch.home_team_id]?.short_name || 'HOME'}</p>
                    <Input
                      type="number"
                      min="0"
                      value={resultData.home_score}
                      onChange={(e) => setResultData({...resultData, home_score: e.target.value})}
                      className="w-20 mx-auto mt-2 text-center text-2xl font-bold rounded-none"
                      data-testid="home-score-input"
                    />
                  </div>
                  <span className="text-2xl font-bold text-gray-300">-</span>
                  <div className="text-center flex-1">
                    <img 
                      src={teamsMap[selectedMatch.away_team_id]?.logo_url || 'https://placehold.co/60x60/09090b/FFF?text=A'} 
                      alt="" 
                      className="w-16 h-16 mx-auto mb-2 object-contain"
                    />
                    <p className="font-heading font-bold">{teamsMap[selectedMatch.away_team_id]?.short_name || 'AWAY'}</p>
                    <Input
                      type="number"
                      min="0"
                      value={resultData.away_score}
                      onChange={(e) => setResultData({...resultData, away_score: e.target.value})}
                      className="w-20 mx-auto mt-2 text-center text-2xl font-bold rounded-none"
                      data-testid="away-score-input"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-[#009E60] hover:bg-[#008050] text-white rounded-none" data-testid="save-result-btn">
                  <Check className="w-4 h-4 mr-2" /> Save Result & Update Standings
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Matches List */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-24 bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : filteredMatches.length > 0 ? (
          <div className="space-y-4">
            {filteredMatches.map(match => (
              <div key={match.match_id} className="bg-white border border-gray-200 p-4 flex flex-col md:flex-row items-center justify-between gap-4" data-testid={`admin-match-card-${match.match_id}`}>
                <div className="text-sm text-gray-500 w-32">
                  <p className="font-medium">{formatDate(match.match_date)}</p>
                  <p>{match.match_time || 'TBD'}</p>
                  <p className="text-xs text-[#009E60]">{compsMap[match.competition_id]?.short_name}</p>
                </div>
                
                <div className="flex items-center gap-4 flex-1 justify-center">
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-bold">{teamsMap[match.home_team_id]?.short_name || 'HOME'}</span>
                    <img 
                      src={teamsMap[match.home_team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=H'} 
                      alt="" 
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  
                  {match.status === 'completed' ? (
                    <div className="font-heading font-bold text-2xl">
                      {match.home_score} - {match.away_score}
                    </div>
                  ) : (
                    <span className="text-gray-400">VS</span>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <img 
                      src={teamsMap[match.away_team_id]?.logo_url || 'https://placehold.co/40x40/09090b/FFF?text=A'} 
                      alt="" 
                      className="w-10 h-10 object-contain"
                    />
                    <span className="font-heading font-bold">{teamsMap[match.away_team_id]?.short_name || 'AWAY'}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-bold uppercase ${
                    match.status === 'completed' ? 'bg-[#009E60] text-white' : 
                    match.status === 'scheduled' ? 'bg-blue-500 text-white' : 'bg-orange-500 text-white'
                  }`}>
                    {match.status}
                  </span>
                  {match.status !== 'completed' && (
                    <Button
                      size="sm"
                      onClick={() => openResultDialog(match)}
                      className="bg-[#009E60] hover:bg-[#008050] text-white rounded-none"
                      data-testid={`enter-result-${match.match_id}`}
                    >
                      Enter Result
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(match)}
                    className="rounded-none"
                    data-testid={`edit-match-${match.match_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(match.match_id)}
                    className="rounded-none"
                    data-testid={`delete-match-${match.match_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No Matches Yet</h3>
            <p className="text-gray-500 mb-4">Add your first match to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMatches;
